import type { EducationClass } from '../educationClasses';
import type { DayOfWeek, Lesson } from '../educationPlans';

export type ScheduleClass = {
  educationalClassId: number;
  curriculumId: number;
};

export enum ScheduleConstraintCode {
  ROOM_CONFLICT = 'ROOM_CONFLICT',
  ROOM_SUBJECTS_CONFLICT = 'ROOM_SUBJECTS_CONFLICT',
  TEACHER_CONFLICT = 'TEACHER_CONFLICT',
  TEACHER_QUALIFICATION_CONFLICT = 'TEACHER_QUALIFICATION_CONFLICT',
  CURRICULUM_CONFLICT = 'CURRICULUM_CONFLICT',
  TEACHER_ROOM_CONFLICT = 'TEACHER_ROOM_CONFLICT',
  FIRST_LESSON_GAP_CONFLICT = 'FIRST_LESSON_GAP_CONFLICT',
  GAP_BETWEEN_LESSONS_CONFLICT = 'GAP_BETWEEN_LESSONS_CONFLICT',
  REPEATING_SUBJECT_IN_DAY_CONFLICT = 'REPEATING_SUBJECT_IN_DAY_CONFLICT',
  REPEATING_SUBJECT_IN_DAY_SOFT_CONFLICT = 'REPEATING_SUBJECT_IN_DAY_SOFT_CONFLICT',
  PHYSICAL_TRAINING_BEFORE_HIGH_MENTAL_LOAD_SUBJECTS_CONFLICT = 'PHYSICAL_TRAINING_BEFORE_HIGH_MENTAL_LOAD_SUBJECTS_CONFLICT',
  MORE_THAN_TWO_CONSECUTIVE_HIGH_MENTAL_LOAD_SUBJECTS_CONFLICT = 'MORE_THAN_TWO_CONSECUTIVE_HIGH_MENTAL_LOAD_SUBJECTS_CONFLICT',
  DIFFICULTY_DISTRIBUTION_MODEL = 'DIFFICULTY_DISTRIBUTION_MODEL',
  DAILY_DIFFICULTY_DISTRIBUTION_CONFLICT = 'DAILY_DIFFICULTY_DISTRIBUTION_CONFLICT',
  TEACHER_AVAILABILITY_CONFLICT = 'TEACHER_AVAILABILITY_CONFLICT',
  SPLIT_LESSON_CONFLICT = 'SPLIT_LESSON_CONFLICT',
  SUBJECT_AREA = 'SUBJECT_AREA',
  SATURDAY_LESSON_PENALTY = 'SATURDAY_LESSON_PENALTY',
  NON_UNIFORM_LESSONS_DISTRIBUTION = 'NON_UNIFORM_LESSONS_DISTRIBUTION',
  TEACHER_GAPS_CONFLICT = 'TEACHER_GAPS_CONFLICT',
  TEACHER_MIX_CONFLICT = 'TEACHER_MIX_CONFLICT',
}

export type ScheduleConstraintMode = 'ENABLED' | 'DISABLED';

export type ScheduleConfigurationStatus = 'DRAFT' | 'PUBLISHED';

export type ScheduleConstraintCategory = 'LOGICAL' | 'SANPIN';

export type DifficultyDistributionModelCode = 'HILL' | 'SNAKE';

export type ScheduleConstraint = {
  code: ScheduleConstraintCode;
  mode: ScheduleConstraintMode;
  category?: ScheduleConstraintCategory;
};

export type ScheduleConstraintWeight = {
  code: ScheduleConstraintCode;
  hardScore: number;
  softScore: number;
  category: ScheduleConstraintCategory;
};

export type ScheduleConfiguration = {
  id: number;
  name: string;
  maxLessonsPerDayId?: number;
  useBaseMaxLessonPerDay?: boolean;
  educationalClasses: ScheduleClass[];
  constraints?: ScheduleConstraint[];
  constraintWeights?: ScheduleConstraintWeight[];
  status?: ScheduleConfigurationStatus;
  difficultyDistributionModelCode?: DifficultyDistributionModelCode;
};

export type ScheduleConfigurationsListRequest = {
  page?: number;
  size?: number;
};

export type PrePublishCheckCode =
  | 'NO_ROOM_FOR_SUBJECT'
  | 'NO_TEACHER_FOR_CLASS_SUBJECT'
  | 'NO_TEACHER_FOR_GROUP_CLASS'
  | 'CLASS_CAPACITY_EXCEEDED'
  | 'ROOM_CAPACITY_EXCEEDED'
  | 'MISSING_OR_INVALID_SUBJECT_DIFFICULTY'
  | 'NO_TEACHER_FOR_MANDATORY_IMPLICIT_LESSON'
  | 'NO_ROOM_FOR_MANDATORY_IMPLICIT_LESSON';

type BasePrePublishCheckDto<TCode extends PrePublishCheckCode> = {
  code: TCode;
};

export type NoRoomForSubject = BasePrePublishCheckDto<'NO_ROOM_FOR_SUBJECT'> & {
  subjectId: number;
  subjectName: string;
};

export type NoTeacherForClassSubject = BasePrePublishCheckDto<'NO_TEACHER_FOR_CLASS_SUBJECT'> & {
  subjectId: number;
  subjectName: string;
  educationalClass: EducationClass;
};

export type NoTeacherForGroupClass = BasePrePublishCheckDto<'NO_TEACHER_FOR_GROUP_CLASS'> & {
  subjectId: number;
  subjectName: string;
  educationalClass: EducationClass;
  groupId: number;
  groupName: string;
};

export type ClassCapacityExceeded = BasePrePublishCheckDto<'CLASS_CAPACITY_EXCEEDED'> & {
  maxAvailableSlots: number;
  totalClassHours: number;
  educationalClass: EducationClass;
};

export type RoomCapacityExceeded = BasePrePublishCheckDto<'ROOM_CAPACITY_EXCEEDED'> & {
  subjectId: number;
  subjectName: string;
  totalSubjectHours: number;
  availableSlots: number;
};

export type MissingOrInvalidSubjectDifficulty =
  BasePrePublishCheckDto<'MISSING_OR_INVALID_SUBJECT_DIFFICULTY'> & {
    subjectId: number;
    subjectName: string;
    educationalClass: EducationClass;
    difficulty: null;
  };

export type NoTeacherForMandatoryImplicitLesson =
  BasePrePublishCheckDto<'NO_TEACHER_FOR_MANDATORY_IMPLICIT_LESSON'> & {
    subjectId: number;
    subjectName: string;
    educationalClass: EducationClass;
    dayOfWeek: DayOfWeek;
    lessonIndex: Lesson;
  };

export type NoRoomForMandatoryImplicitLesson =
  BasePrePublishCheckDto<'NO_ROOM_FOR_MANDATORY_IMPLICIT_LESSON'> & {
    subjectId: number;
    subjectName: string;
    educationalClass: EducationClass;
    dayOfWeek: DayOfWeek;
    lessonIndex: Lesson;
  };

export type ScheduleConfigurationPrePublishCheckDto =
  | NoRoomForSubject
  | NoTeacherForClassSubject
  | NoTeacherForGroupClass
  | ClassCapacityExceeded
  | RoomCapacityExceeded
  | MissingOrInvalidSubjectDifficulty
  | NoTeacherForMandatoryImplicitLesson
  | NoRoomForMandatoryImplicitLesson;
